<script setup>
import { computed, ref } from 'vue'
import { trans } from 'laravel-vue-i18n'
import { getFormColor, getColleagueColor } from '../../../composables/useReservationsCalendar.js'
import { getInitials } from '../../../../../helpers.js'

// ─── Time helpers ─────────────────────────────────────────────────
const HOUR_PX = 48
const DAY_START = 7
const DAY_END = 20
const HOURS = DAY_END - DAY_START // 13

function parseTime(t) {
    const [h, m] = (t ?? '00:00').split(':').map(Number)
    return h + (m || 0) / 60
}
function fmtTime(t) {
    return t ? t.substring(0, 5).replace(/^0/, '') : ''
}
function eventTop(timeStart) {
    return Math.max(0, parseTime(timeStart) - DAY_START) * HOUR_PX
}
function eventHeight(timeStart, timeEnd) {
    const s = Math.max(0, parseTime(timeStart) - DAY_START)
    const e = Math.min(HOURS, parseTime(timeEnd) - DAY_START)
    return Math.max(20, (e - s) * HOUR_PX)
}

// ─── Color helpers ────────────────────────────────────────────────
function colorWithAlpha(hex, alpha) {
    return hex + Math.round(alpha * 255).toString(16).padStart(2, '0')
}
function eventBg(reservation) {
    const color = getFormColor(reservation.reservation_forms)
    if (reservation.state === 'rejected') return 'transparent'
    if (reservation.state === 'pending') return colorWithAlpha(color, 0.12)
    return color
}
function eventColor(reservation) {
    const color = getFormColor(reservation.reservation_forms)
    if (reservation.state === 'rejected') return '#9BA1AB'
    if (reservation.state === 'pending') return color
    return '#fff'
}
function eventBorder(reservation) {
    const color = getFormColor(reservation.reservation_forms)
    if (reservation.state === 'rejected') return '1px dashed #C9CDD4'
    if (reservation.state === 'pending') return `1px dashed ${color}`
    return 'none'
}

// ─── Props & emits ────────────────────────────────────────────────
const props = defineProps({
    reservations:        { type: Array, default: () => [] },
    allEmployees:        { type: Array, default: () => [] },
    visibleFormIds:      { type: Set, default: () => new Set() },
    visibleColleagueIds: { type: Set, default: () => new Set() },
    selectedDate:        Object, // moment
})

const emit = defineEmits(['open-reservation', 'hover-reservation', 'reservation-changed'])

// ─── Drag & drop ──────────────────────────────────────────────────
const scrollBodyRef = ref(null)
const gridBodyRef = ref(null)
const dragState = ref(null)
const dropIndicator = ref(null)

function calcDragMinutes(evt) {
    if (!dragState.value || !gridBodyRef.value || !scrollBodyRef.value) return null
    const { offsetY, duration } = dragState.value
    const rect = gridBodyRef.value.getBoundingClientRect()
    const relY = evt.clientY - rect.top + scrollBodyRef.value.scrollTop - offsetY
    const rawHours = DAY_START + Math.max(0, relY) / HOUR_PX
    const snappedMin = Math.round(rawHours * 4) * 15
    const maxStart = DAY_END * 60 - Math.round(duration * 60)
    const startMin = Math.max(DAY_START * 60, Math.min(maxStart, snappedMin))
    const endMin = Math.min(DAY_END * 60, startMin + Math.round(duration * 60))
    return { startMin, endMin }
}

function onDragStart(evt, reservation) {
    const duration = reservation.time_end
        ? parseTime(reservation.time_end) - parseTime(reservation.time_start)
        : 1
    dragState.value = { reservation, offsetY: evt.offsetY, duration }
    evt.dataTransfer.effectAllowed = 'move'
    evt.dataTransfer.setData('text/plain', String(reservation.id))
}

function onDragEnd() {
    dragState.value = null
    dropIndicator.value = null
}

function onColumnDragOver(evt) {
    evt.preventDefault()
    const mins = calcDragMinutes(evt)
    if (!mins || !dragState.value) return
    const { startMin, endMin } = mins
    const fmtMin = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
    dropIndicator.value = {
        top: (startMin / 60 - DAY_START) * HOUR_PX,
        height: Math.max(24, (endMin - startMin) / 60 * HOUR_PX),
        timeLabel: `${fmtMin(startMin)} – ${fmtMin(endMin)}`,
        color: getFormColor(dragState.value.reservation.reservation_forms),
        name: dragState.value.reservation.name,
    }
}

function onColumnDrop(evt) {
    evt.preventDefault()
    const mins = calcDragMinutes(evt)
    if (!mins || !dragState.value) return
    const { startMin, endMin } = mins
    const toTime = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}:00`
    emit('reservation-changed', {
        reservation: dragState.value.reservation,
        newDate: dateStr.value,
        newTimeStart: toTime(startMin),
        newTimeEnd: toTime(endMin),
    })
    dragState.value = null
    dropIndicator.value = null
}

// ─── Computed ─────────────────────────────────────────────────────
const dateStr = computed(() => props.selectedDate?.format('YYYY-MM-DD') ?? '')

const isToday = computed(() => {
    const today = new Date()
    const d = props.selectedDate
    return d && d.year() === today.getFullYear() && d.month() === today.getMonth() && d.date() === today.getDate()
})

const timedEvents = computed(() =>
    props.reservations.filter(r =>
        r.date === dateStr.value &&
        r.time_start &&
        props.visibleFormIds.has(r.id_reservation_forms)
    )
)

const allDayEvents = computed(() =>
    props.reservations.filter(r =>
        r.date_start && r.date_end &&
        dateStr.value >= r.date_start &&
        dateStr.value <= r.date_end &&
        props.visibleFormIds.has(r.id_reservation_forms)
    )
)

// Columns: allEmployees filtered by visibleColleagueIds, fallback to all, fallback to single virtual
const cols = computed(() => {
    const filtered = props.allEmployees.filter(e => props.visibleColleagueIds.has(e.id))
    const base = filtered.length ? filtered : props.allEmployees
    return base.length ? base : [{ id: null, name: trans('reservations.filter_all') }]
})

function eventsForCol(col) {
    const colIds = cols.value.map(c => c.id)
    return timedEvents.value.filter(r => {
        if (col.id === null) return true
        // no worker or worker not in any col → first col
        if (!r.id_reservation_workers || !colIds.includes(r.id_reservation_workers)) {
            return col.id === cols.value[0].id
        }
        return r.id_reservation_workers === col.id
    })
}

const nowTop = computed(() => {
    const now = new Date()
    return (now.getHours() + now.getMinutes() / 60 - DAY_START) * HOUR_PX
})

const nowLabel = computed(() => {
    const now = new Date()
    return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`
})

const hourMarkers = computed(() => {
    const markers = []
    for (let h = 0; h <= HOURS; h++) {
        markers.push({ h, label: `${DAY_START + h}:00`, top: h * HOUR_PX })
    }
    return markers
})

const colIndex = (col) => props.allEmployees.findIndex(e => e.id === col.id)

function onHoverEnter(reservation, event) {
    emit('hover-reservation', { reservation, x: event.clientX, y: event.clientY })
}
function onHoverLeave() {
    emit('hover-reservation', null)
}
</script>

<template>
    <div class="flex-1 min-h-0 flex flex-col overflow-hidden bg-white dark:bg-neutral-900">

        <!-- All-day strip -->
        <div v-if="allDayEvents.length" class="flex border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-800/40 flex-shrink-0">
            <div class="w-20 border-r border-neutral-100 dark:border-neutral-800 text-[10px] text-neutral-400 dark:text-neutral-500 flex items-center justify-center flex-shrink-0">
                {{ $t('reservations.cal_all_day') }}
            </div>
            <div class="flex-1 flex flex-wrap gap-1 p-1.5 min-h-[32px]">
                <div
                    v-for="r in allDayEvents"
                    :key="r.id"
                    class="flex items-center gap-1 px-2 h-6 rounded text-[11px] font-medium cursor-pointer hover:opacity-80 transition-opacity"
                    :style="{
                        background: eventBg(r),
                        color: eventColor(r),
                        border: eventBorder(r),
                    }"
                    @click="emit('open-reservation', r)"
                >
                    {{ r.name }}
                </div>
            </div>
        </div>

        <!-- Body -->
        <div ref="scrollBodyRef" class="flex-1 overflow-y-auto flex min-h-0">

            <!-- Time column -->
            <div
                class="w-20 flex-shrink-0 border-r border-neutral-100 dark:border-neutral-800 relative"
                :style="{ height: HOURS * HOUR_PX + 'px' }"
            >
                <div
                    v-for="({ label, top }, i) in hourMarkers"
                    :key="i"
                    class="absolute right-3 text-[10px] text-neutral-400 dark:text-neutral-600 select-none"
                    :style="{ top: (i === 0 ? 0 : top - 7) + 'px' }"
                >
                    {{ label }}
                </div>
            </div>

            <!-- Columns area -->
            <div ref="gridBodyRef" class="flex-1 relative" :style="{ height: HOURS * HOUR_PX + 'px' }">

                <!-- Sticky column headers -->
                <div class="sticky top-0 z-10 flex bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800">
                    <div
                        v-for="(col, ci) in cols"
                        :key="col.id ?? 'all'"
                        class="flex items-center gap-2 px-3 py-2 border-r border-neutral-100 dark:border-neutral-800 last:border-r-0"
                        :style="{ width: 100 / cols.length + '%' }"
                    >
                        <div
                            v-if="col.id !== null"
                            class="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                            :style="{ background: getColleagueColor(colIndex(col)) }"
                        >
                            {{ getInitials(col.name) }}
                        </div>
                        <span class="text-[12px] font-medium text-neutral-700 dark:text-neutral-300 truncate">{{ col.name }}</span>
                    </div>
                </div>

                <!-- Hour lines -->
                <div
                    v-for="h in HOURS"
                    :key="h"
                    class="absolute left-0 right-0 bg-neutral-100 dark:bg-neutral-800"
                    style="height: 1px"
                    :style="{ top: h * HOUR_PX + 'px' }"
                />

                <!-- Now indicator -->
                <div
                    v-if="isToday"
                    class="absolute left-0 right-0 z-10 pointer-events-none"
                    :style="{ top: nowTop + 'px' }"
                >
                    <div class="h-[2px] bg-red-500 relative">
                        <div class="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-red-500"/>
                        <span class="absolute left-3 -top-[9px] text-[10px] text-red-500 font-semibold bg-white dark:bg-neutral-900 px-0.5">
                            {{ nowLabel }}
                        </span>
                    </div>
                </div>

                <!-- Per-colleague columns -->
                <div
                    v-for="(col, ci) in cols"
                    :key="col.id ?? 'all'"
                    class="absolute top-0 bottom-0"
                    :style="{
                        left: (ci * 100 / cols.length) + '%',
                        width: (100 / cols.length) + '%',
                    }"
                    @dragover="onColumnDragOver"
                    @drop="onColumnDrop"
                >
                    <!-- Right border -->
                    <div
                        v-if="ci < cols.length - 1"
                        class="absolute top-0 bottom-0 right-0 w-px bg-neutral-100 dark:bg-neutral-800"
                    />

                    <!-- Drop indicator -->
                    <div
                        v-if="dropIndicator"
                        class="absolute rounded-[6px] pointer-events-none z-20 flex flex-col px-2 py-1 overflow-hidden"
                        :style="{
                            top: dropIndicator.top + 'px',
                            height: dropIndicator.height + 'px',
                            left: '4px',
                            right: '6px',
                            background: dropIndicator.color + '28',
                            border: `2px dashed ${dropIndicator.color}`,
                        }"
                    >
                        <div class="text-[10px] font-bold leading-none mb-0.5" :style="{ color: dropIndicator.color }">
                            {{ dropIndicator.timeLabel }}
                        </div>
                        <div class="text-[11px] font-medium leading-tight truncate" :style="{ color: dropIndicator.color }">
                            {{ dropIndicator.name }}
                        </div>
                    </div>

                    <!-- Events -->
                    <div
                        v-for="r in eventsForCol(col)"
                        :key="r.id"
                        draggable="true"
                        class="absolute cursor-pointer rounded-[6px] px-2 py-1.5 transition-shadow hover:shadow-md overflow-hidden"
                        @dragstart="onDragStart($event, r)"
                        @dragend="onDragEnd"
                        :style="{
                            top: eventTop(r.time_start) + 'px',
                            height: eventHeight(r.time_start, r.time_end) + 'px',
                            left: '4px',
                            right: '6px',
                            background: eventBg(r),
                            color: eventColor(r),
                            border: eventBorder(r),
                        }"
                        @click="emit('open-reservation', r)"
                        @mouseenter="onHoverEnter(r, $event)"
                        @mouseleave="onHoverLeave"
                    >
                        <div class="text-[10px] font-semibold leading-none mb-0.5" style="opacity: .9">
                            {{ fmtTime(r.time_start) }} – {{ fmtTime(r.time_end) }}
                        </div>
                        <div class="text-[13px] font-medium leading-tight truncate">{{ r.name }}</div>
                        <div v-if="r.state === 'pending'" class="text-[10px] font-semibold mt-0.5 opacity-75">
                            {{ $t('reservations.state_pending_label') }}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>
